import { useEffect, useState } from 'react';
import type { LobbyState, WSClientMsg } from '@geoguess/shared';
import { REGIONS } from '@geoguess/shared';
import { Crown, Minus, Plus } from 'lucide-react';
import type { AuthedUser } from '../discord.js';
import { PlayerAvatar } from '../components/PlayerAvatar.js';
import { Logo } from '../components/Logo.js';
import { Spinner } from '../components/Spinner.js';

export function LobbyScreen({
  lobby,
  me,
  isHost,
  send,
}: {
  lobby: LobbyState;
  me: AuthedUser;
  isHost: boolean;
  send: (msg: WSClientMsg) => void;
}) {
  const players = Object.values(lobby.players);
  const region = lobby.region;
  const [starting, setStarting] = useState(false);
  useEffect(() => {
    if (lobby.phase !== 'lobby') setStarting(false);
  }, [lobby.phase]);

  return (
    <div className="bg-hero flex h-full flex-col">
      {/* ── Top bar ─────────────────────────────────────────────── */}
      <header className="flex items-center justify-between px-5 pt-5 pb-3">
        <div className="flex items-center gap-2.5">
          <Logo size={28} />
          <h1 className="text-[22px] font-bold tracking-tight">Wo Ist?</h1>
        </div>
        {region && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-panel/80 px-3 py-1 text-xs font-medium text-fg backdrop-blur">
            <span>{region.emoji}</span>
            <span className="text-muted">{region.name}</span>
          </span>
        )}
      </header>

      <div className="grid min-h-0 flex-1 gap-4 px-4 pb-2 lg:grid-cols-[240px_1fr]">
        {/* ── Players (left grouped list) ───────────────────────── */}
        <aside className="flex flex-col overflow-hidden rounded-2xl bg-panel">
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <h2 className="text-[13px] font-semibold uppercase tracking-wider text-muted">
              Spieler
            </h2>
            <span className="text-xs text-muted-2">{players.length}</span>
          </div>
          <ul className="row-divide flex-1 overflow-auto">
            {players.map((p) => {
              const isMe = p.userId === me.userId;
              const isThisHost = p.userId === lobby.hostUserId;
              return (
                <li
                  key={p.userId}
                  className="flex items-center gap-3 px-4 py-2.5"
                >
                  <PlayerAvatar
                    name={p.name}
                    url={p.avatar || undefined}
                    size={36}
                    ring={isMe}
                    online={p.connected}
                    dim={!p.connected}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate font-medium">{p.name}</span>
                      {isThisHost && <Crown className="h-3.5 w-3.5 shrink-0 text-warn" />}
                    </div>
                    {!p.connected && (
                      <div className="text-[12px] text-muted-2">getrennt</div>
                    )}
                  </div>
                  {isMe && (
                    <span className="rounded-full bg-accent-soft px-2 py-0.5 text-[11px] font-semibold text-accent">
                      Du
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </aside>

        {/* ── Settings (right) ──────────────────────────────────── */}
        <main className="flex min-h-0 flex-col gap-4 overflow-auto pr-2">
          <section>
            <h3 className="mb-2 px-1 text-[13px] font-semibold uppercase tracking-wider text-muted">
              Region
            </h3>
            <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
              {REGIONS.map((r) => {
                const selected = region?.id === r.id;
                const disabled = !isHost;
                return (
                  <button
                    key={r.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => send({ t: 'set_region', regionId: r.id })}
                    className={`press flex items-center gap-3 rounded-2xl px-4 py-4 text-left transition ${
                      selected
                        ? 'bg-accent-soft ring-2 ring-accent'
                        : 'bg-panel ring-1 ring-border hover:bg-panel-2'
                    } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
                  >
                    <span
                      className={`grid h-12 w-12 shrink-0 place-items-center rounded-xl text-2xl ${
                        selected ? 'bg-bg/40' : 'bg-panel-2'
                      }`}
                    >
                      {r.emoji}
                    </span>
                    <div className="min-w-0">
                      <div className="font-semibold">{r.name}</div>
                      <div className="text-[12px] text-muted">Skala {r.scaleKm} km</div>
                    </div>
                    {selected && (
                      <span className="ml-auto h-2.5 w-2.5 rounded-full bg-accent shadow-[0_0_0_4px_rgba(10,132,255,0.25)]" />
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          <section>
            <h3 className="mb-2 px-1 text-[13px] font-semibold uppercase tracking-wider text-muted">
              Einstellungen
            </h3>
            <div className="row-divide rounded-2xl bg-panel">
              <SettingRow
                label="Runden"
                value={String(lobby.totalRounds)}
                input={
                  <Stepper
                    value={lobby.totalRounds}
                    min={1}
                    max={10}
                    step={1}
                    disabled={!isHost}
                    onChange={(v) => send({ t: 'set_rounds', total: v })}
                  />
                }
              />
              <SettingRow
                label="Zeit pro Runde"
                value={`${lobby.roundTimeSec}s`}
                input={
                  <input
                    type="range"
                    min={20}
                    max={180}
                    step={10}
                    value={lobby.roundTimeSec}
                    disabled={!isHost}
                    onChange={(e) =>
                      send({ t: 'set_round_time', sec: Number(e.target.value) })
                    }
                    className="w-44"
                  />
                }
              />
            </div>
          </section>

          <p className="px-1 text-[12px] text-muted-2">
            {isHost
              ? 'Du bist Host — wähl eine Region und starte das Spiel.'
              : 'Warte auf den Host. Region & Einstellungen werden vom Host bestimmt.'}
          </p>
        </main>
      </div>

      {/* ── Sticky footer CTA ─────────────────────────────────── */}
      <footer className="sticky bottom-0 border-t border-border bg-bg/80 px-4 py-3 backdrop-blur">
        <button
          type="button"
          disabled={!isHost || !region || players.length < 1 || starting}
          onClick={() => {
            setStarting(true);
            send({ t: 'start_game' });
          }}
          className="press inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-accent px-5 py-3.5 text-[16px] font-semibold text-white shadow-[0_8px_24px_rgba(10,132,255,0.35)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:bg-panel-2 disabled:text-muted-2 disabled:shadow-none"
        >
          {starting ? (
            <>
              <Spinner size={18} />
              <span>Lädt…</span>
            </>
          ) : (
            <span>{isHost ? 'Spiel starten' : 'Warte auf Host…'}</span>
          )}
        </button>
      </footer>
    </div>
  );
}

function SettingRow({
  label,
  value,
  input,
}: {
  label: string;
  value: string;
  input: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3.5">
      <div className="min-w-0">
        <div className="text-[15px] font-medium">{label}</div>
        <div className="text-[12px] text-muted-2">{value}</div>
      </div>
      <div className="shrink-0">{input}</div>
    </div>
  );
}

function Stepper({
  value,
  min,
  max,
  step,
  disabled,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
  onChange: (v: number) => void;
}) {
  const dec = () => !disabled && onChange(Math.max(min, value - step));
  const inc = () => !disabled && onChange(Math.min(max, value + step));
  return (
    <div className="inline-flex items-center overflow-hidden rounded-full bg-panel-2">
      <button
        type="button"
        onClick={dec}
        disabled={disabled || value <= min}
        className="press grid h-8 w-9 place-items-center text-fg disabled:opacity-40"
        aria-label="weniger"
      >
        <Minus className="h-4 w-4" />
      </button>
      <span className="min-w-[2.5ch] text-center font-mono text-sm tabular-nums">
        {value}
      </span>
      <button
        type="button"
        onClick={inc}
        disabled={disabled || value >= max}
        className="press grid h-8 w-9 place-items-center text-fg disabled:opacity-40"
        aria-label="mehr"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}
